import { useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { SalonPhoto } from '../api/use-marketplace';

/**
 * How one Salunas photo fills its frame — mirrors the admin's layout editor
 * (libs/admin/features/settings … hero-layout-editor.tsx heroImageStyle):
 * object-fit + object-position, and for `cover` a zoom that scales around
 * the focus point. `contain` ignores zoom (the whole photo stays visible).
 */
export function photoStyle(photo: Pick<SalonPhoto, 'fit' | 'position' | 'zoom'>): CSSProperties {
  const objectPosition = photo.position || '50% 50%';
  const fit = photo.fit === 'contain' ? 'contain' : 'cover';
  const zoom = typeof photo.zoom === 'number' && Number.isFinite(photo.zoom) ? photo.zoom : 100;
  if (fit === 'contain' || zoom <= 100) return { objectFit: fit, objectPosition };
  return {
    objectFit: 'cover',
    objectPosition,
    transform: `scale(${Math.min(200, zoom) / 100})`,
    transformOrigin: objectPosition,
  };
}

/** Minimum horizontal travel (px) for a swipe to change the slide. */
const SWIPE_THRESHOLD = 40;

interface SalonHeroMediaProps {
  name: string;
  photos?: SalonPhoto[] | null;
  coverImageUrl: string | null;
  coverImagePosition?: string | null;
}

/**
 * The salon page header image(s), rendered inside the cover <section>.
 *
 * - 2+ Salunas photos → a slideshow (arrows, dots, swipe, ←/→ when focused;
 *   no autoplay).
 * - 1 photo → that photo with its layout, no controls.
 * - none → the resolved cover (website hero / operator cover) as before.
 *
 * The section's gradient overlay and back link render after this component,
 * so the controls sit on z-20 above them (the overlay is pointer-events-none).
 */
export function SalonHeroMedia({ name, photos, coverImageUrl, coverImagePosition }: SalonHeroMediaProps) {
  const list = (photos ?? []).filter((p) => p && typeof p.url === 'string' && p.url);

  if (list.length > 1) return <SalonHeroCarousel name={name} photos={list} />;

  if (list.length === 1) {
    return (
      <img
        src={list[0].url}
        alt={name}
        style={photoStyle(list[0])}
        className="absolute inset-0 w-full h-full"
        data-testid="salon-hero-image"
      />
    );
  }

  if (!coverImageUrl) return null;
  return (
    <img
      src={coverImageUrl}
      style={{ objectPosition: coverImagePosition ?? undefined }}
      alt={name}
      className="absolute inset-0 w-full h-full object-cover"
      data-testid="salon-hero-image"
    />
  );
}

function SalonHeroCarousel({ name, photos }: { name: string; photos: SalonPhoto[] }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const total = photos.length;

  const go = (next: number) => setIndex(((next % total) + total) % total);
  const prev = () => go(index - 1);
  const next = () => go(index + 1);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      next();
    }
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    // Ignore presses that start on a control (arrow / dot buttons).
    if ((e.target as HTMLElement).closest?.('button')) return;
    swipeStart.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!start || !Number.isFinite(e.clientX) || !Number.isFinite(start.x)) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) next();
    else prev();
  };

  const arrowClass =
    'absolute top-[40%] -translate-y-1/2 z-20 inline-flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-background/85 backdrop-blur-sm text-foreground border border-border/40 shadow-sm hover:bg-background focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/60 transition-colors';

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label={t('salon.photos.carousel', { name })}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        swipeStart.current = null;
      }}
      className="absolute inset-0 select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-foreground/60"
      style={{ touchAction: 'pan-y' }}
      data-testid="salon-carousel"
    >
      <div
        className="flex h-full transition-transform duration-500 ease-out motion-reduce:transition-none"
        style={{ transform: `translateX(-${index * 100}%)` }}
        aria-live="polite"
        data-testid="salon-carousel-track"
      >
        {photos.map((photo, i) => (
          <div
            key={`${photo.url}-${i}`}
            role="group"
            aria-roledescription="slide"
            aria-label={t('salon.photos.slide', { current: i + 1, total })}
            aria-hidden={i !== index}
            className="relative h-full w-full shrink-0 overflow-hidden"
            data-testid="salon-carousel-slide"
            data-active={i === index ? 'true' : 'false'}
          >
            <img
              src={photo.url}
              alt={i === 0 ? name : ''}
              loading={i === 0 ? 'eager' : 'lazy'}
              draggable={false}
              style={photoStyle(photo)}
              className="absolute inset-0 w-full h-full"
            />
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={prev}
        aria-label={t('salon.photos.previous')}
        className={`${arrowClass} left-3 md:left-6`}
        data-testid="salon-carousel-prev"
      >
        <ChevronLeft className="w-5 h-5" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={next}
        aria-label={t('salon.photos.next')}
        className={`${arrowClass} right-3 md:right-6`}
        data-testid="salon-carousel-next"
      >
        <ChevronRight className="w-5 h-5" aria-hidden="true" />
      </button>

      {/* Dots sit top-right: the header card overlaps the bottom of the hero. */}
      <div className="absolute top-4 right-4 md:top-6 md:right-6 z-20 flex items-center gap-1.5 px-2.5 py-2 rounded-full bg-background/85 backdrop-blur-sm border border-border/40">
        {photos.map((photo, i) => (
          <button
            key={`dot-${photo.url}-${i}`}
            type="button"
            onClick={() => go(i)}
            aria-label={t('salon.photos.goTo', { n: i + 1 })}
            aria-current={i === index ? 'true' : undefined}
            className={`h-2 rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/60 ${
              i === index ? 'w-5 bg-foreground' : 'w-2 bg-foreground/35 hover:bg-foreground/60'
            }`}
            data-testid="salon-carousel-dot"
          />
        ))}
      </div>
    </div>
  );
}
